import {useState, useCallback, useEffect} from 'react'
import {format, subMonths} from 'date-fns'
import {EnhancementProductStat, EnhancementReportStat} from '../../../models/enhancement/enhancement-report-stats'
import {useAppDispatch} from '../../../redux/store'
import {OnTimeSubmissionReport} from '../../../models/enhancement/on-time-submission-report'
import {EnhancementReportSearch} from './useEnhancementsFilter'
import {enhancementReportService} from '../../../services/enhancementReportService'
import {BaseResponse} from '../../../types/base-response'
import {showErrorMessage} from '../../../redux/reducers/snackbarReducer'
import UserErrorMessage from '../../../utils/errorFilter'
import {PeriodType} from '../PeriodTypeSelector'

const getDefaultDateRange = () => {
  const today = new Date()
  const twelveMonthsAgo = subMonths(today, 12)
  return {
    today,
    twelveMonthsAgo,
    dateFrom: format(twelveMonthsAgo, 'yyyy-MM-dd'),
    dateTo: format(today, 'yyyy-MM-dd')
  }
}

export function useEnhancementsReport() {
  const dispatch = useAppDispatch()
  const [showFilters, setShowFilters] = useState(true)
  const [resetForm, setResetForm] = useState(false)
  const [reportData, setReportData] = useState<EnhancementReportStat[]>([])
  const [productData, setProductData] = useState<EnhancementProductStat[]>([])
  const [onTimeSubmissionData, setOnTimeSubmissionData] = useState<OnTimeSubmissionReport | null>(null)
  const [dataLastUpdated, setDataLastUpdated] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState(false)
  const [hasSearched, setHasSearched] = useState(false)
  const [selectedPeriodType, setSelectedPeriodType] = useState<PeriodType>(
    PeriodType.PAY_PERIOD
  )


  const initialDates = getDefaultDateRange()

  const [requestTypeFilter, setRequestTypeFilter] = useState<string[]>()
  const [filters, setFilters] = useState<EnhancementReportSearch>({
    departmentId: null,
    teamId: null,
    dateFrom: initialDates.dateFrom,
    dateTo: initialDates.dateTo,
  })

  
  const fetchDataLastUpdated = useCallback(async (): Promise<void> => {
    try {
      const response = await enhancementReportService.getDataLastUpdated()
      setDataLastUpdated(response.dataLastUpdated)
    } catch (err: any) {
      console.warn('Failed to fetch data last updated:', err)
      setDataLastUpdated('error')
    }
  }, [])
  
  useEffect(() => {
    fetchDataLastUpdated()
  }, [])

  const fetchAllEnhancementReportData = useCallback(async (): Promise<EnhancementReportStat[]> => {
    try {
      const response = await enhancementReportService.getEnhancementStats(filters)
      if (response.enhancementStats) {
        setReportData(response.enhancementStats)
      }
      if (response.enhancementProductStats) {
        setProductData(response.enhancementProductStats)
      }
      return response.enhancementStats || []
    } catch (err: any) {
      if (err.response?.data) {
        const response: BaseResponse = err.response.data
        if (response.errors) {
          response.errors.forEach(error => {
            dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
          })
        } else {
          dispatch(showErrorMessage(<UserErrorMessage name="CurrentlyUnavailable" />))
        }
      } else {
        dispatch(showErrorMessage(<UserErrorMessage name="CurrentlyUnavailable" />))
      }
      setReportData([])
      setProductData([])
      return []
    }
  }, [filters, dispatch])

  const fetchOnTimeSubmissionReport = useCallback(async (): Promise<OnTimeSubmissionReport | null> => {
    try {
      const response = await enhancementReportService.getOnTimeSubmissionReport(filters)

      if (response?.monthlyOnTimeSubmissionPercentage &&
        response?.payPeriodOnTimeSubmissionPercentage) {
        const submissionData: OnTimeSubmissionReport = {
          monthlyOnTimeSubmissionPercentage: response.monthlyOnTimeSubmissionPercentage,
          payPeriodOnTimeSubmissionPercentage: response.payPeriodOnTimeSubmissionPercentage
        }

        setOnTimeSubmissionData(submissionData)
        return submissionData
      }

      setOnTimeSubmissionData(null)
      return null
    } catch (err: any) {
      if (err.response?.data) {
        const response: BaseResponse = err.response.data
        if (response.errors) {
          response.errors.forEach(error => {
            dispatch(showErrorMessage(<UserErrorMessage name={error.name} />))
          })
        } else {
          dispatch(showErrorMessage(<UserErrorMessage name="CurrentlyUnavailable" />))
        }
      } else {
        dispatch(showErrorMessage(<UserErrorMessage name="CurrentlyUnavailable" />))
      }
      setOnTimeSubmissionData(null)
      return null
    }
  }, [filters, dispatch])

  const handleFilterClick = useCallback(() => {
    setShowFilters(prev => !prev)
  }, [])

  const handleRefreshClick = useCallback(() => {
    setResetForm(prev => !prev)
    setShowFilters(true)
    setHasSearched(false)
    setSelectedPeriodType(PeriodType.PAY_PERIOD)
    
    setReportData([])
    setProductData([])
    setOnTimeSubmissionData(null)

    const refreshedDates = getDefaultDateRange()

    setFilters({
      departmentId: null,
      teamId: null,
      dateFrom: refreshedDates.dateFrom,
      dateTo: refreshedDates.dateTo,
    })
  }, [])

  const handleFilterChange = useCallback((search: EnhancementReportSearch) => {
    setFilters(search)
  }, [])

  const handleSearch = useCallback(() => {
    setShowFilters(false)
    setHasSearched(true)
    setIsLoading(true)

    Promise.all([
      fetchAllEnhancementReportData(),
      fetchOnTimeSubmissionReport(),
      fetchDataLastUpdated()
    ]).finally(() => {
      setIsLoading(false)
    })
  }, [fetchAllEnhancementReportData, fetchOnTimeSubmissionReport, fetchDataLastUpdated])

  const handleTileClick = useCallback((requestType: string) => {
    setRequestTypeFilter(prevRequestTypeFilter => {
      const currentTypes = prevRequestTypeFilter || []
      let newSelectedTypes: string[]

      if (requestType === 'Total') {
        newSelectedTypes = []
      } else if (currentTypes.includes(requestType)) {
        newSelectedTypes = currentTypes.filter(type => type !== requestType)
      } else {
        newSelectedTypes = [...currentTypes, requestType]
      }
      return newSelectedTypes
    })
  }, [])

  return {
    showFilters,
    resetForm,
    reportData,
    productData,
    onTimeSubmissionData,
    dataLastUpdated,
    isLoading,
    hasSearched,
    currentFilters: filters,
    requestTypeFilter,
    selectedPeriodType,
    handleFilterClick,
    handleRefreshClick,
    handleFilterChange,
    handleSearch,
    handleTileClick,
  }
}